/**
 * TeamSystem Impact - API Client
 * Axios instance configurato con interceptors
 */

import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { logger } from '@/config/logger';

const API_URL = import.meta.env.VITE_API_URL || '/api';

export const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper per ottenere token dallo store Zustand (via localStorage)
const getTokenFromStore = (): string | null => {
  try {
    const stored = localStorage.getItem('teamsystem-impact-auth');
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed.token || null;
    }
  } catch (e) {
    logger.error('Error reading token from localStorage:', e);
  }
  return null;
};

// Helper per ottenere azienda ID dallo store
const getAziendaIdFromStore = (): string | null => {
  try {
    const stored = localStorage.getItem('teamsystem-impact-auth');
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed.utente?.azienda?.id || null;
    }
  } catch (e) {
    logger.error('Error reading azienda from localStorage:', e);
  }
  return null;
};

// Request interceptor: aggiungi JWT token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Ottieni token dallo store
    const token = getTokenFromStore();

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Aggiungi azienda corrente
    const aziendaId = getAziendaIdFromStore();
    if (aziendaId && config.headers) {
      config.headers['x-azienda-id'] = aziendaId;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Helper per ottenere refresh token da localStorage
const getRefreshTokenFromStore = (): string | null => {
  try {
    const stored = localStorage.getItem('teamsystem-impact-auth');
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed.refreshToken || null;
    }
  } catch (e) {
    logger.error('Error reading refreshToken from localStorage:', e);
  }
  return null;
};

// Helper per aggiornare token in localStorage (senza importare lo store Zustand)
const updateTokensInStore = (token: string, refreshToken: string) => {
  try {
    const stored = localStorage.getItem('teamsystem-impact-auth');
    if (stored) {
      const parsed = JSON.parse(stored);
      parsed.token = token;
      parsed.refreshToken = refreshToken;
      localStorage.setItem('teamsystem-impact-auth', JSON.stringify(parsed));
    }
  } catch (e) {
    logger.error('Error updating tokens in localStorage:', e);
  }
};

// Mutex per evitare refresh multipli simultanei
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

const onRefreshed = (token: string) => {
  refreshSubscribers.forEach(cb => cb(token));
  refreshSubscribers = [];
};

const addRefreshSubscriber = (cb: (token: string) => void) => {
  refreshSubscribers.push(cb);
};

// Response interceptor: gestisci errori con token refresh automatico
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Se 401 e non è già un retry, prova refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Non tentare refresh sulle chiamate auth stesse
      const url = originalRequest.url || '';
      if (url.includes('/auth/refresh') || url.includes('/auth/login') || url.includes('/auth/dev-login')) {
        localStorage.removeItem('teamsystem-impact-auth');
        window.location.href = '/login';
        return Promise.reject(error);
      }

      const refreshToken = getRefreshTokenFromStore();
      if (!refreshToken) {
        logger.debug('No refresh token available, redirecting to login');
        localStorage.removeItem('teamsystem-impact-auth');
        window.location.href = '/login';
        return Promise.reject(error);
      }

      // Se un refresh è già in corso, accoda la richiesta
      if (isRefreshing) {
        return new Promise((resolve) => {
          addRefreshSubscriber((newToken: string) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(apiClient(originalRequest));
          });
        });
      }

      isRefreshing = true;

      try {
        logger.debug('Attempting token refresh...');
        const response = await axios.post(`${API_URL}/auth/refresh`, { refreshToken });
        const { jwt: newToken, refreshToken: newRefreshToken } = response.data;

        // Aggiorna localStorage
        updateTokensInStore(newToken, newRefreshToken);

        // Aggiorna anche lo store Zustand (lazy import per evitare circular dependency)
        const { useAuthStore } = await import('@/store/authStore');
        const state = useAuthStore.getState();
        if (state.utente) {
          useAuthStore.setState({ token: newToken, refreshToken: newRefreshToken });
        }

        logger.debug('Token refreshed successfully');
        isRefreshing = false;

        // Notifica le richieste in coda
        onRefreshed(newToken);

        // Riprova la richiesta originale con il nuovo token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        logger.error('Token refresh failed, redirecting to login');
        isRefreshing = false;
        refreshSubscribers = [];

        // Refresh fallito: pulisci e redirect
        localStorage.removeItem('teamsystem-impact-auth');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Altri errori
    return Promise.reject(error);
  }
);

export const getAuthToken = (): string | null => {
  return getTokenFromStore();
};

export const isAuthenticated = (): boolean => {
  return !!getAuthToken();
};
